/**
 * How the app tells you something: statuses, notices, the three big states,
 * and toasts.
 *
 * `<State>` is deliberately one component for empty, error and loading. Ten
 * screens used to hand-roll all three, so "nothing here yet", "that failed"
 * and "hold on" arrived in three different visual languages and the operator
 * had to read the words to work out which had happened.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { AlertCircle, CheckCircle2, Inbox, Loader2 } from "lucide-react";
import { cx } from "./Button";

/* ── Badge ───────────────────────────────────────────────────────────── */

export type Tone = "neutral" | "live" | "success" | "danger" | "warning" | "info";

export function Badge({ tone = "neutral", dot = true, children }: { tone?: Tone; dot?: boolean; children: ReactNode }) {
  return <span className={cx("badge", tone !== "neutral" && `badge-${tone}`, !dot && "badge-plain")}>{children}</span>;
}

/* ── Notice — an inline banner attached to the thing it is about ─────── */

export function Notice({ tone = "neutral", icon, children }: { tone?: Tone; icon?: ReactNode; children: ReactNode }) {
  return (
    <div className={cx("notice", tone !== "neutral" && tone !== "live" && tone !== "success" && `notice-${tone}`)} role={tone === "danger" ? "alert" : undefined}>
      {icon}
      <div>{children}</div>
    </div>
  );
}

/* ── State — empty, error, loading ───────────────────────────────────── */

export function State({
  kind = "empty", title, blurb, icon, action,
}: {
  kind?: "empty" | "error" | "loading";
  title: string;
  blurb?: string;
  icon?: ReactNode;
  action?: ReactNode;
}) {
  const fallback = kind === "error" ? <AlertCircle /> : kind === "loading" ? <Loader2 className="spin" /> : <Inbox />;
  return (
    <div className={cx("state", kind === "error" && "state-error")} role={kind === "error" ? "alert" : "status"}>
      {icon ?? fallback}
      <b>{title}</b>
      {blurb && <p>{blurb}</p>}
      {action}
    </div>
  );
}

/** Placeholder blocks that hold the shape of what is loading. */
export function Skeleton({ height = 16, width, radius }: { height?: number | string; width?: number | string; radius?: number }) {
  return <span className="skeleton" style={{ display: "block", height, width: width ?? "100%", borderRadius: radius }} />;
}

/* ── Toast ───────────────────────────────────────────────────────────── */

interface Toast {
  id: number;
  text: string;
  tone: "neutral" | "danger";
}

interface ToastApi {
  /** Confirmation of something that already happened. */
  notify: (text: string) => void;
  /** Something failed. Stays up longer — a failure needs reading time. */
  fail: (text: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(0);
  // Timers are tracked so a provider unmount (or a fast route change) does not
  // leave setState calls scheduled against a gone component.
  const timers = useRef<Array<ReturnType<typeof setTimeout>>>([]);

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const push = useCallback((text: string, tone: Toast["tone"]) => {
    const id = nextId.current++;
    setToasts((all) => [...all, { id, text, tone }]);
    timers.current.push(setTimeout(() => setToasts((all) => all.filter((t) => t.id !== id)), tone === "danger" ? 6000 : 3200));
  }, []);

  const api = useMemo<ToastApi>(() => ({
    notify: (text) => push(text, "neutral"),
    fail: (text) => push(text, "danger"),
  }), [push]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/* `polite` so a confirmation never interrupts what is being read. */}
      <div className="toasts" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={cx("toast", t.tone === "danger" && "toast-danger")}>
            {t.tone === "danger" ? <AlertCircle /> : <CheckCircle2 />}
            <span>{t.text}</span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}
